import { Box, Link as ChakraLink, Flex, Grid, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { locales, useLanguageSwitcher } from '#features/i18n';
import {
  FOOTER_CONTACT_EMAIL,
  FOOTER_ROUTES,
  FOOTER_SOCIALS,
} from '#features/layout/constants/footerLinks';
import { DiscordIcon, GooglePlayIcon, Link, RedditIcon, XIcon } from '#features/ui';
import { GameTrademarksNotice } from '#features/ui/components/GameTrademarksNotice';
import { IconButton } from '#features/ui/components/IconButton';
import { playStoreUrl } from '#features/app-install';

const socials = [
  {
    name: 'discord',
    href: FOOTER_SOCIALS.discord,
    icon: (
      <Flex
        bgColor="fg"
        borderRadius="full"
        color="black"
        fontSize="20px"
        boxSize="36px"
        justifyContent="center"
        alignItems="center"
      >
        <DiscordIcon />
      </Flex>
    ),
  },
  {
    name: 'x',
    href: FOOTER_SOCIALS.x,
    icon: <XIcon fontSize="36px" />,
  },
  {
    name: 'reddit',
    href: 'https://www.reddit.com/r/teamplay/s/LFaLN9ZNcK',
    icon: <RedditIcon style={{ width: '1em', height: '1em', fontSize: '1em' }} />,
  },
  {
    name: 'googlePlay',
    href: playStoreUrl('landing-footer'),
    icon: (
      <Flex
        bgColor="fg"
        borderRadius="full"
        color="black"
        fontSize="20px"
        boxSize="36px"
        justifyContent="center"
        alignItems="center"
      >
        <GooglePlayIcon />
      </Flex>
    ),
  },
];

type LanguageProps = {
  text: string;
  locale: keyof typeof locales;
};

const Language = ({ text, locale }: LanguageProps) => {
  const { currentLocale, changeLocale } = useLanguageSwitcher();
  const isActive = currentLocale === locale;

  return (
    <Text
      fontSize="sm"
      {...(isActive
        ? { color: 'fg' }
        : {
            color: 'fg.muted',
            cursor: 'pointer',
            _hover: { color: 'fg' },
          })}
      onClick={() => changeLocale(locale)}
    >
      {text}
    </Text>
  );
};

export const LandingFooter = () => {
  const { t } = useLingui();

  return (
    <Box
      as="footer"
      borderTop="1px solid"
      borderColor="whiteAlpha.100"
      mt={8}
      pt={{ base: 8, md: 12 }}
      pb={{ base: 6, md: 8 }}
    >
      <Grid
        templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }}
        gap={{ base: 8, md: 6 }}
        mb={10}
      >
        {/* Brand */}
        <Flex direction="column" gap={3}>
          <Text fontWeight="800" fontSize="lg">
            teamplay.gg
          </Text>
          <Text fontSize="sm" color="fg.muted" lineHeight="1.7" maxW="220px">
            {t`v2.footer.tagline`}
          </Text>
          <Flex gap={2} mt={1}>
            {socials.map(({ name, href, icon }) => (
              <IconButton
                key={name}
                variant="ghost"
                rounded="full"
                fontSize="36px"
                size="sm"
                asChild
              >
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {icon}
                </a>
              </IconButton>
            ))}
          </Flex>
        </Flex>

        {/* Product */}
        <Flex direction="column" gap={2}>
          <Text fontWeight="700" fontSize="sm" mb={1} color="fg.emphasized">
            {t`v2.footer.product`}
          </Text>
          <Link to={FOOTER_ROUTES.blog} fontSize="sm" color="fg.muted" _hover={{ color: 'fg' }}>
            {t`footer.blog`}
          </Link>
          <Link to={FOOTER_ROUTES.faq} fontSize="sm" color="fg.muted" _hover={{ color: 'fg' }}>
            FAQ
          </Link>
          <Link
            to={FOOTER_ROUTES.discordBot}
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'fg' }}
          >
            Discord App
          </Link>
          <ChakraLink
            href={playStoreUrl('landing-footer')}
            target="_blank"
            rel="noopener noreferrer"
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'fg' }}
          >
            Google Play
          </ChakraLink>
        </Flex>

        {/* Company */}
        <Flex direction="column" gap={2}>
          <Text fontWeight="700" fontSize="sm" mb={1} color="fg.emphasized">
            {t`v2.footer.company`}
          </Text>
          <Link
            to={FOOTER_ROUTES.joinTheTeam}
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'fg' }}
          >
            {t`v2.footer.joinTeam`}
          </Link>
          <Link
            to={FOOTER_ROUTES.partnerWithUs}
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'fg' }}
          >
            {t`v2.footer.partnerWithUs`}
          </Link>
          <ChakraLink
            href={`mailto:${FOOTER_CONTACT_EMAIL}`}
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'fg' }}
          >
            {t`footer.contactUs`}
          </ChakraLink>
        </Flex>

        {/* Legal + Language */}
        <Flex direction="column" gap={2}>
          <Text fontWeight="700" fontSize="sm" mb={1} color="fg.emphasized">
            {t`v2.footer.legal`}
          </Text>
          <Link to={FOOTER_ROUTES.terms} fontSize="sm" color="fg.muted" _hover={{ color: 'fg' }}>
            {t`footer.terms`}
          </Link>
          <Link to={FOOTER_ROUTES.privacy} fontSize="sm" color="fg.muted" _hover={{ color: 'fg' }}>
            {t`footer.privacyPolicy`}
          </Link>
          <ChakraLink
            href="#"
            className="cky-banner-element"
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'fg' }}
          >
            {t`footer.cookieSettings`}
          </ChakraLink>
          <Flex gap={3} mt={2}>
            <Language text="EN" locale="en" />
            <Language text="RU" locale="ru" />
          </Flex>
        </Flex>
      </Grid>

      {/* Игровые трейдмарки: универсальная строка + именные Riot/Epic (см. компонент). */}
      <GameTrademarksNotice mb={8} />

      {/* Bottom bar */}
      <Flex
        justifyContent="space-between"
        alignItems="center"
        pt={6}
        borderTop="1px solid"
        borderColor="whiteAlpha.50"
      >
        <Text fontSize="xs" color="fg.muted">
          © {new Date().getFullYear()} Teamplay.gg
        </Text>
        <Text fontSize="xs" color="fg.muted">
          {t`v2.footer.madeFor`}
        </Text>
      </Flex>
    </Box>
  );
};
